import { useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { makeAutoObservable } from 'mobx';
import { Button, Modal as ModalAntd } from 'antd';

import { Form, FormField } from '@/Core/UI/Form';
import { createFormContext } from '@/Core/UI/Form/Providers';
import { restClient } from '@/Core';
import { useNotification } from '@/Ship/Hooks/useNotification';

export const useFormModal = ({
    fields = {},
    modalProps = {},
    initialValues = {},
    restUri = null,
    method = 'post',
    depFn = () => {},
    restUriReplaceParams = (uri) => uri,
    requestSuccessMessage = '',
    onBeforeSubmit = null,
    onAfterSuccessRequest = null,
    onAfterErrorRequest = null,
    formTemplate = null,
    showExtraSubmitButton = false,
    extraSubmitButtonText = '',
    onExtraSubmit = null,
}) => {
    const { showSuccess, showError } = useNotification();

    const localStorageRef = useRef();
    if (!localStorageRef.current) {
        localStorageRef.current = makeAutoObservable({
            isModalOpen: false,
            setIsModalOpen(isModalOpen) {
                this.isModalOpen = isModalOpen;
            },
        });
    }
    const localStorage = localStorageRef.current;

    const [isLoading, setIsLoading] = useState(false);

    const formContextRef = useRef();
    if (!formContextRef.current) {
        formContextRef.current = createFormContext({
            fields,
            initialValues,
            eventHandlers: {
                onSubmit: {
                    handleSubmit: async ({ values }) => {
                        if (typeof onBeforeSubmit === 'function') {
                            const shouldContinue = onBeforeSubmit({ values, formContext: formContextRef.current });
                            if (shouldContinue === false) return;
                        }

                        if (!restUri) {
                            console.error('"restUri" parameter not set.');
                            return;
                        }

                        setIsLoading(true);
                        localStorage.setIsModalOpen(false);

                        // Normalize values: replace objects with @id to just the @id string
                        const normalizedValues = Object.fromEntries(
                            Object.entries(values).map(([key, value]) => {
                                if (value && typeof value === 'object' && !Array.isArray(value) && value['@id']) {
                                    return [key, value['@id']];
                                }
                                return [key, value];
                            })
                        );

                        try {
                            const response = await restClient[method](restUriReplaceParams(restUri), { json: normalizedValues });
                            showSuccess('Успіх', requestSuccessMessage);

                            if (typeof onAfterSuccessRequest === 'function') {
                                onAfterSuccessRequest({ values, formContext: formContextRef.current, localStorage, response });
                            }
                        } catch (error) {
                            const errResponse = error?.response;
                            const description =
                                errResponse?.['hydra:description'] ||
                                errResponse?.message ||
                                error?.message ||
                                'Сталася помилка';
                            showError('Помилка', description);

                            if (typeof onAfterErrorRequest === 'function') {
                                onAfterErrorRequest({ values, formContext: formContextRef.current, localStorage });
                            }
                        } finally {
                            setIsLoading(false);
                        }
                    },
                },
            },
        });
    }
    const formContext = formContextRef.current;

    const resolvedModalProps = {
        cancelText: 'Закрити',
        okText: 'Зберегти',
        destroyOnClose: true,
        ...modalProps,
        onOk: typeof modalProps.onOk === 'function' ? modalProps.onOk : () => formContext.submit(),
        onCancel: typeof modalProps.onCancel === 'function' ? modalProps.onCancel : () => localStorage.setIsModalOpen(false),
    };

    if (showExtraSubmitButton && extraSubmitButtonText && typeof onExtraSubmit === 'function' && !resolvedModalProps.footer) {
        resolvedModalProps.footer = [
            <Button
                key="extraSubmit"
                type="default"
                onClick={async () => {
                    const result = await formContext.submit();
                    if (result) onExtraSubmit({ values: formContext.values, formContext, localStorage });
                }}
                loading={isLoading}
            >
                {extraSubmitButtonText}
            </Button>,
            <Button key="cancel" onClick={resolvedModalProps.onCancel}>
                {resolvedModalProps.cancelText}
            </Button>,
            <Button key="submit" type="primary" onClick={resolvedModalProps.onOk} loading={isLoading}>
                {resolvedModalProps.okText}
            </Button>,
        ];
    }

    const Template = formTemplate;

    const Modal = observer(() => {
        depFn({ formContext });

        return (
            <ModalAntd
                open={localStorage.isModalOpen}
                confirmLoading={isLoading}
                {...resolvedModalProps}
            >
                {Template ? (
                    typeof Template === 'function' ? Template({ formContext }) : <Template formContext={formContext} />
                ) : (
                    <Form formContext={formContext}>
                        {Object.keys(fields).map((name) => (
                            <FormField name={name} key={name} />
                        ))}
                    </Form>
                )}
            </ModalAntd>
        );
    });

    return {
        Modal,
        localStorage,
        formContext,
        isLoading,
    };
};
